import { useMemo } from "react";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import {
  spiritualProfileRepository,
  type AddSpiritualProfileEntityPayload,
  type UpdateSpiritualProfileEntityPayload,
} from "../repositories/spiritualProfileRepository";

export const spiritualProfileQueryKeys = {
  allProfiles: ["spiritual-profile", "me"] as const,
  profile: (scopeKey: string) => ["spiritual-profile", "me", scopeKey] as const,
  lines: (scopeKey: string) => ["spiritual-profile", "lines", scopeKey] as const,
  entities: (scopeKey: string) =>
    ["spiritual-profile", "entities", scopeKey] as const,
  analytics: (terreiroId: string) =>
    ["terreiro", terreiroId, "spiritual-profile", "analytics"] as const,
};

const resolveScope = (
  terreiroId?: string,
  mediumId?: string,
): { scope: { terreiroId?: string; mediumId?: string }; scopeKey: string } => {
  if (terreiroId) {
    return {
      scope: { terreiroId },
      scopeKey: `terreiro:${terreiroId}`,
    };
  }

  if (mediumId) {
    return {
      scope: { mediumId },
      scopeKey: `medium:${mediumId}`,
    };
  }

  return {
    scope: {},
    scopeKey: "none",
  };
};

export const useSpiritualProfileScope = () => {
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  return useMemo(
    () =>
      resolveScope(
        typeof cachedTerreiro?.id === "string" ? cachedTerreiro.id : undefined,
        typeof cachedMedium?.id === "string" ? cachedMedium.id : undefined,
      ),
    [cachedMedium?.id, cachedTerreiro?.id],
  );
};

export const useSpiritualProfileQuery = () => {
  const { scope, scopeKey } = useSpiritualProfileScope();

  return useQuery({
    queryKey: spiritualProfileQueryKeys.profile(scopeKey),
    queryFn: () => spiritualProfileRepository.getMyProfile(scope),
    enabled: Boolean(scope.terreiroId || scope.mediumId),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 30,
    retry: 1,
    refetchOnMount: "always",
  });
};

export const useSpiritualLinesQuery = () => {
  const { scope, scopeKey } = useSpiritualProfileScope();

  return useQuery({
    queryKey: spiritualProfileQueryKeys.lines(scopeKey),
    queryFn: () => spiritualProfileRepository.getLines(scope),
    enabled: Boolean(scope.terreiroId || scope.mediumId),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    retry: 1,
    refetchOnMount: "always",
  });
};

export const useSpiritualEntitiesQuery = () => {
  const { scope, scopeKey } = useSpiritualProfileScope();

  return useQuery({
    queryKey: spiritualProfileQueryKeys.entities(scopeKey),
    queryFn: () => spiritualProfileRepository.getEntities(scope),
    enabled: Boolean(scope.terreiroId || scope.mediumId),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    retry: 1,
    refetchOnMount: "always",
  });
};

export const useSpiritualAnalyticsSummaryQuery = (
  terreiroId?: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: spiritualProfileQueryKeys.analytics(terreiroId ?? "none"),
    queryFn: () => spiritualProfileRepository.getAnalyticsSummary(terreiroId!),
    enabled: enabled && !!terreiroId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
    refetchOnMount: "always",
  });
};

// Returned to the mutation so callers that navigate next already see the list and the hub's "N registros" updated.
const refreshSpiritualProfiles = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: spiritualProfileQueryKeys.allProfiles,
    refetchType: "all",
  });

export const useAddSpiritualEntityMutation = () => {
  const queryClient = useQueryClient();
  const { scope } = useSpiritualProfileScope();

  return useMutation({
    mutationFn: (payload: Omit<AddSpiritualProfileEntityPayload, "terreiroId" | "mediumId">) =>
      spiritualProfileRepository.addEntity({
        ...payload,
        ...scope,
      }),
    onSuccess: () => refreshSpiritualProfiles(queryClient),
  });
};

export const useUpdateSpiritualEntityMutation = () => {
  const queryClient = useQueryClient();
  const { scope } = useSpiritualProfileScope();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateSpiritualProfileEntityPayload;
    }) => spiritualProfileRepository.updateEntity(id, payload, scope),
    onSuccess: () => refreshSpiritualProfiles(queryClient),
  });
};

export const useRemoveSpiritualEntityMutation = () => {
  const queryClient = useQueryClient();
  const { scope } = useSpiritualProfileScope();

  return useMutation({
    mutationFn: (id: string) => spiritualProfileRepository.removeEntity(id, scope),
    onSuccess: () => refreshSpiritualProfiles(queryClient),
  });
};
